import * as Sentry from '@sentry/vue'
import { useUserStore } from '@/stores/user/profile'
import axios from 'axios'
import { getActivePinia } from 'pinia'
import { chunkErrorDetected, isChunkLoadError } from '@/utils/chunkError'

type CaptureTags = Record<string, string | number | boolean>
type CaptureExtra = Record<string, unknown>

interface CaptureContext {
  tags?: CaptureTags
  extra?: CaptureExtra
  level?: Sentry.SeverityLevel
}

export function captureError(error: unknown, context: CaptureContext = {}): void {
  // Callers throughout the app wrap router.push() in try/catch and report
  // whatever it throws — including a stale-chunk navigation failure, which
  // router.onError already recognizes and surfaces via the reload prompt
  // (chunkErrorDetected). Route it the same way here instead of also
  // reporting it as an unrelated error from every individual call site.
  if (isChunkLoadError(error)) {
    chunkErrorDetected.value = true
    console.warn('captureError: suppressed a known stale-chunk error', error)
    return
  }

  Sentry.withScope((scope) => {
    if (context.level) {
      scope.setLevel(context.level)
    }
    if (getActivePinia()) {
      try {
        const userStore = useUserStore()
        if (userStore.user) {
          scope.setUser({
            id: String(userStore.user.id),
            email: userStore.user.email ?? undefined,
            username: userStore.user.name ?? undefined
          })
        }
      } catch (e) {}
    }

    if (typeof axios.isAxiosError === 'function' && axios.isAxiosError(error)) {
      const status = error.response?.status ?? 'NETWORK_ERROR'
      const url = error.config?.url ?? 'unknown'
      const method = error.config?.method?.toUpperCase() ?? 'unknown'

      scope.setTag('http.status', String(status))
      scope.setTag('http.url', url)
      scope.setTag('http.method', method)

      let requestPayload = error.config?.data
      if (typeof requestPayload === 'string') {
        try {
          requestPayload = JSON.parse(requestPayload)
        } catch {
          /* skip */
        }
      }

      scope.setContext('API Details', {
        request_url: url,
        request_method: method,
        status_code: status,
        request_payload: requestPayload,
        response_body: error.response?.data
      })

      if (status === 422) {
        scope.setContext('Validation Errors', {
          errors: error.response?.data?.errors || error.response?.data
        })
      }
    }

    if (context.tags) {
      scope.setTags(context.tags as Record<string, string>)
    }
    if (context.extra) {
      scope.setExtras(context.extra)
    }

    Sentry.captureException(error)
  })
}
